import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import superAdminApi from '../../utils/superAdminApi';
import toast from 'react-hot-toast';
import { FiPlus, FiToggleLeft, FiToggleRight, FiHome, FiArrowRight } from 'react-icons/fi';

const emptyForm = { name: '', address: '', phone: '', email: '' };

export default function Shops() {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchShops = useCallback(async () => {
    setLoading(true);
    try {
      const r = await superAdminApi.get('/shops');
      setShops(r.data.shops);
    } catch { toast.error('Failed to load shops'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchShops(); }, [fetchShops]);

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error('Shop name is required');
    setSaving(true);
    try {
      await superAdminApi.post('/shops', form);
      toast.success('Shop created');
      setShowModal(false);
      setForm(emptyForm);
      fetchShops();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create shop');
    }
    setSaving(false);
  };

  const toggleShop = async (id, e) => {
    e.stopPropagation();
    try {
      await superAdminApi.put(`/shops/${id}/toggle`);
      fetchShops();
    } catch { toast.error('Failed to update shop'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#64748b' }}>{shops.length} shop(s)</div>
        <button className="btn btn-accent" onClick={() => setShowModal(true)}>
          <FiPlus size={15} /> New Shop
        </button>
      </div>

      <div className="card">
        {loading ? <div className="loading"><div className="spinner"></div></div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Shop</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {shops.length === 0 && (
                  <tr><td colSpan={6}>
                    <div className="empty-state">
                      <div className="empty-state-icon"><FiHome size={40} /></div>
                      <h3>No shops yet</h3>
                      <p>Create the first shop to onboard a tenant</p>
                    </div>
                  </td></tr>
                )}
                {shops.map(shop => (
                  <tr key={shop._id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/superadmin/shops/${shop._id}`)}>
                    <td style={{ fontWeight: 600 }}>{shop.name}</td>
                    <td>{shop.phone || '—'}</td>
                    <td>{shop.email || '—'}</td>
                    <td>
                      {shop.isActive
                        ? <span className="badge badge-success">Active</span>
                        : <span className="badge badge-danger">Disabled</span>}
                    </td>
                    <td>{new Date(shop.createdAt).toLocaleDateString('en-AE', { timeZone: 'Asia/Riyadh', day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-outline btn-sm btn-icon" title="Toggle Active"
                          onClick={(e) => toggleShop(shop._id, e)}>
                          {shop.isActive ? <FiToggleRight size={16} /> : <FiToggleLeft size={16} />}
                        </button>
                        <button className="btn btn-outline btn-sm btn-icon" title="Open"
                          onClick={(e) => { e.stopPropagation(); navigate(`/superadmin/shops/${shop._id}`); }}>
                          <FiArrowRight size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">🏪 New Shop</h3>
              <button className="btn btn-outline btn-sm btn-icon" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Shop Name <span className="required">*</span></label>
                <input className="form-control" placeholder="e.g. Al Noor Tailors"
                  value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Address</label>
                <textarea className="form-control" rows={2} value={form.address}
                  onChange={e => setForm({ ...form, address: e.target.value })} />
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input className="form-control" value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-control" value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-accent" onClick={handleCreate} disabled={saving}>
                {saving ? 'Creating...' : 'Create Shop'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
